import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectDemandRequest } from '../../../../../selectors/dataPayment';
import { useTrackRealtorDemand } from '../../../utils';
import { getDemandRequestSteps } from './getDemandRequestSteps';
import { DEMAND_MESSAGES, DEMAND_SOURCE } from '../../../constants';
import { bindActionCreators } from 'redux';
import { sendDemandRequest } from '../../../../../actions/dataPayment';
import { getRealtorFormUrl } from '../../../../../utils/getRealtorFormUrl';
import { selectIsWebview } from '../../../../../selectors/page';
import { EAction } from '../../../../../repositories/price-estimator/entities/estimation/CheckEstimationRequestExistsResponseSchema';

export const useDemandRequest = () => {
  const { status, dealTypeRequired, phoneRequired, isError } = useSelector(selectDemandRequest);
  const isWebview = useSelector(selectIsWebview);
  const { trackDemandClick } = useTrackRealtorDemand();
  const [isDemandModalOpen, setIsDemandModalOpen] = useState(false);
  const description = isError ? DEMAND_MESSAGES.error : DEMAND_MESSAGES[status];
  const dispatch = useDispatch();
  const boundActions = useMemo(() => bindActionCreators({ sendDemandRequest }, dispatch), [dispatch]);

  const toggleOpenDemandModal = useCallback(() => {
    setIsDemandModalOpen(state => !state);
  }, []);

  /**
   * Список шагов дополнительной формы заявки
   */
  const demandSteps = useMemo(
    () => getDemandRequestSteps(phoneRequired, dealTypeRequired),
    [phoneRequired, dealTypeRequired],
  );

  /**
   * Хэндлер сабмита в модальном окне дополнительной формы
   */
  const handleModalSubmit = useCallback(() => {
    toggleOpenDemandModal();
    boundActions.sendDemandRequest();
  }, [toggleOpenDemandModal, boundActions]);

  /**
   * Хэндлер открытия страницы стандартной формы заявки
   */
  const handleRealtorFormAction = useCallback(() => {
    trackDemandClick();
    const { url } = getRealtorFormUrl({ isWebview, cianSource: DEMAND_SOURCE.ESTATE_CALCULATOR_BANNER });
    window.open(url, '_blank');
  }, [isWebview, trackDemandClick]);

  /**
   * Хэндлер отправки заявки без дополнительной формы
   */
  const handleSendRequestAction = useCallback(() => {
    trackDemandClick();
    boundActions.sendDemandRequest();
  }, [trackDemandClick, boundActions]);

  /**
   * Хэндлер отправки заявки с открытием дополнительной формы
   */
  const handleSendRequestExtraQuestionsAction = useCallback(() => {
    trackDemandClick();
    toggleOpenDemandModal();
  }, [trackDemandClick, toggleOpenDemandModal]);

  /**
   * Получение основного экшена блока заявки риэлтору
   */
  const mainAction = useMemo(() => {
    switch (true) {
      /** Вариант с отправкой заявки через дополнительную форму (дозапрашиваем данные) **/
      case status === EAction.SendRequest && Boolean(demandSteps.length):
        return handleSendRequestExtraQuestionsAction;
      /** Вариант с отправкой заявке без дополнительной формы **/
      case status === EAction.SendRequest:
        return handleSendRequestAction;
      /** Если заявка уже была отправлена ранее - кнопка действия отсутствует **/
      case status === EAction.RequestSent:
        return undefined;
      /** При варианте с открытием стандартной формы оценки и при любых других статусов - открываем страницу формы **/
      case status === EAction.RealtorForm:
      default:
        return handleRealtorFormAction;
    }
  }, [status, demandSteps, handleSendRequestExtraQuestionsAction, handleSendRequestAction, handleRealtorFormAction]);

  return {
    mainAction,
    handleModalSubmit,
    isDemandModalOpen,
    toggleOpenDemandModal,
    description,
    demandSteps,
  };
};
